import { useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Share2, Copy, Check, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export function ShareButton({ kind, refId, title, summary, size = "default", className = "" }) {
  const [open, setOpen] = useState(false);
  const [share, setShare] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const link = share ? `${window.location.origin}/share/${share.token}` : "";
  const message = `🌱 ${title}\n${summary}\n\nFull details: ${link}\n— shared via AgroTerra`;

  const start = async () => {
    setOpen(true);
    if (share) return;
    setBusy(true);
    try {
      const res = await apiClient.post("/share", { kind, ref_id: refId });
      setShare(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy — long-press the link to copy it"); }
  };

  return (
    <>
      <Button data-testid="share-btn" onClick={start} size={size} variant="outline" className={`border-stone-300 ${className}`}>
        <Share2 className="h-4 w-4 mr-2" /> Share with neighbour
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Share with a neighbour</DialogTitle>
            <DialogDescription>Anyone with the link can read this — no account needed.</DialogDescription>
          </DialogHeader>
          {busy ? (
            <div className="flex items-center gap-2 text-sm text-stone-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Creating link…</div>
          ) : share && (
            <div className="space-y-4">
              <pre data-testid="share-message" className="whitespace-pre-wrap text-sm bg-[#FAF9F6] border border-stone-200 rounded-xl p-3 font-sans text-stone-700 max-h-40 overflow-auto">{message}</pre>
              <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl px-3 py-2">
                <span data-testid="share-link" className="text-xs text-stone-600 truncate flex-1 font-mono-tech">{link}</span>
                <button data-testid="share-copy-btn" onClick={copy} className="p-1.5 rounded-lg hover:bg-stone-100 text-[#2D6A4F]">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
              </div>
              <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 h-12 rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold transition-colors">
                <MessageCircle className="h-5 w-5" /> Send on WhatsApp
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
